import React, { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import {
	PageTitle,
	RevenueSpendListTable,
	RevenueSpendCreateModal,
	RevenueSpendEditModal,
	RevenueSpendEditNotesModal,
	RevenueSpendRemoveModal,
	RevenueSpendHistoryListModal,
	IconPlus,
	ToolPanel,
	UiPagination,
	ESortOrder,
	getAccountIsFiredBadge
} from '../components'
import { Filters, FiltersButtonIcon } from '../components/filters'
import { useRevenueSpendList, useRevenueSpendFiltersPage, useRevenueSpendSortPage } from '../hooks'
import * as fetchs from '../fetchs'
import * as lib from '../lib'
import * as state from '../states'
import { IFetchItemsSetup, INoFetch, IRevenueAgencyDropDown, IRevenueSpend, IAccountBuyer, ERoleAccess } from '../types'
import { TAKE, MENU, MENU_CATEGORY } from '../constants'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'


export const RevenueSpendPage = (): React.JSX.Element => {
	const { t } = useTranslation()
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const profile = useAtomValue(state.profileAtom)
	const $access = new lib.Access(profile ?? null)

	const isBuyer = useAtomValue(state.isBuyerAtom)

	const location = useLocation()
	const href = lib.pageHref(location, TAKE)
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const page = lib.skipSearchParams(searchParams.get('page')) ?? 0
	const take = lib.takeSearchParams(searchParams.get('take')) ?? TAKE
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0
	const buyer = isBuyer ?? lib.buyerIdSearchParams(searchParams.get('buyer') ?? '')

	const revenueAgencies = useAtomValue(state.revenueAgenciesAtom)
	const buyerItems = useAtomValue(state.accountBuyersAtom)
	const currentBuyer = buyerItems?.find(item => item.id === buyer) ?? null
	const currentBuyerIsFired = currentBuyer?.isFired
	const currentBuyerTeamReport = currentBuyer?.role?.teamReport
	const keitaroLogin: string = currentBuyer?.extra?.keitaroLogin ?? ''
	const currentBuyerFormat = `{${currentBuyerTeamReport}} - [id: ${currentBuyer?.id}] ${currentBuyer?.auth?.login ?? currentBuyer?.email ?? '?'} (${keitaroLogin ?? ''})`
	const isRevenueSpendPageIndex = !buyer
	const pageTitle = `${t(MENU_CATEGORY.revenue.title)} ${t(MENU.revenueSpend.title)}`
	const pageTitleFormat = `${pageTitle}${!isRevenueSpendPageIndex ? ': ' + currentBuyerFormat : ''}`

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		revenueSpendFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useRevenueSpendFiltersPage(buyer)

	//TODO: check sort output var type ?
	const {
		// sort: sortTmp,
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useRevenueSpendSortPage(buyer)

	const setup: IFetchItemsSetup = {
		filter,
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		skip,
		take
	}
	const { error, items, total, warmUp } = useRevenueSpendList(setup, send)

	const [createRevenueSpend, setCreateRevenueSpend] = useState<boolean>(false)
	const [editRevenueSpend, setEditRevenueSpend] = useState<IRevenueSpend | null>(null)
	const [editNotesRevenueSpend, setEditNotesRevenueSpend] = useState<IRevenueSpend | null>(null)
	const [removeRevenueSpend, setRemoveRevenueSpend] = useState<IRevenueSpend | null>(null)
	const [viewRevenueSpendHistory, setViewRevenueSpendHistory] = useState<IRevenueSpend | null>(null)

	const onCreate = (e: React.MouseEvent) => {
		e.preventDefault()
		setCreateRevenueSpend(true)
	}
	const onEdit = (item: IRevenueSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setEditRevenueSpend(item)
	}
	const onEditNotes = (item: IRevenueSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setEditNotesRevenueSpend(item)
	}
	const onRemove = (item: IRevenueSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setRemoveRevenueSpend(item)
	}

	const onViewRevenueSpendHistory = (item: IRevenueSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setViewRevenueSpendHistory(item)
	}

	return (
		<>
			<PageTitle title={
				<>
					<span>{pageTitleFormat}</span>
					{!!currentBuyerIsFired ? getAccountIsFiredBadge(currentBuyerIsFired, t) : null}
				</>
			}/>

			<div className="card rounded-3 border-0 mb-24">
				<div className="card-body p-25">
					{/*remove CREATE button for 'account' main page (without pre-selected group)*/}
					<div className="d-flex align-items-center justify-content-between pb-20 border-bottom border-color">
						<FiltersButtonIcon
							activeFiltersCount={activeFiltersCount}
							filterFillFactor={filterFillFactor}
							onClick={() => setIsFiltersPanelOpen((val) => !val)}
						/>

						{
							!!onCreate && !!$access.$(ERoleAccess.RevenueSpendCreate)
								// && !isRevenueSpendPageIndex
								? (
									<button
										type="button"
										className="btn btn-primary d-flex align-items-center"
										onClick={onCreate}
									>
										<IconPlus/>
										<span className="d-none d-lg-block">{`${t(ALL_LANG_KEY.revenue_spend_page.btn_add_new)}`}</span>
									</button>
								)
								: null
						}

					</div>

					<Filters
						config={revenueSpendFiltersConfig}
						initialFiltersValue={initialFiltersValue}
						resetFiltersValue={defaultFiltersValue}
						isAutoModeWithDebounce={false}
						isFiltersPanelOpen={isFiltersPanelOpen}
						isFiltersToClear={clearChildFilters}
						setFiltersCB={(value) => {
							//console.log('page.RolePage::setFiltersCB::', { value })
							setUserFilters(value)
						}}
					/>

					<RevenueSpendListTable
						items={items}
						skip={skip}
						sortBy={sortBy}
						setSortBy={setSortBy}
						order={order}
						setOrder={setOrder}
						onEdit={onEdit}
						onEditNotes={onEditNotes}
						onRemove={onRemove}
						onViewHistory={onViewRevenueSpendHistory}
					/>

					<ToolPanel flow="<-">
						<UiPagination
							classesToAdd={{
								wrapper: 'mt-25'
							}}
							page={page}
							take={take}
							total={total}
							href={(page: number) => href(page).tail}
						/>
					</ToolPanel>
				</div>
			</div>

			{
				!!createRevenueSpend && !!$access.$(ERoleAccess.RevenueSpendCreate) && <RevenueSpendCreateModal
					show={createRevenueSpend}
					accounts={buyerItems}
					revenueAgencies={revenueAgencies}
					onClose={() => setCreateRevenueSpend(false)}
					onSave={() => {
						setCreateRevenueSpend(false)
						navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!editRevenueSpend && !!$access.$(ERoleAccess.RevenueSpendModify) && <RevenueSpendEditModal
					item={editRevenueSpend}
					show={!!editRevenueSpend}
					accounts={buyerItems}
					revenueAgencies={revenueAgencies}
					onClose={() => setEditRevenueSpend(null)}
					onSave={() => {
						setEditRevenueSpend(null)
						navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!editNotesRevenueSpend && !!$access.$(ERoleAccess.RevenueSpendList) && <RevenueSpendEditNotesModal
					isViewMode={!$access.$(ERoleAccess.RevenueSpendModify)}
					item={editNotesRevenueSpend}
					show={!!editNotesRevenueSpend}
					onClose={() => setEditNotesRevenueSpend(null)}
					onSave={() => {
						setEditNotesRevenueSpend(null)
						//navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!removeRevenueSpend && !!$access.$(ERoleAccess.RevenueSpendRemove) && <RevenueSpendRemoveModal
					item={removeRevenueSpend}
					show={!!removeRevenueSpend}
					onClose={() => setRemoveRevenueSpend(null)}
					onSave={() => {
						setRemoveRevenueSpend(null)
						navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!viewRevenueSpendHistory && !!$access.$(ERoleAccess.RevenueSpendList) && <RevenueSpendHistoryListModal
					revenueSpend={viewRevenueSpendHistory}
					show={!!viewRevenueSpendHistory}
					take={take}
					onClose={() => {
						setViewRevenueSpendHistory(null)
						// navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

		</>
	)
}
